import { MIME_TYPES } from "@excalidraw/common";
import type { MaybePromise } from "@excalidraw/common/utility-types";

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import pdfWorkerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

import { blobToArrayBuffer, canvasToBlob, getFileHandleType } from "./blob";
import { fileSave } from "./filesystem";
import { serializeAsJSON, type JSONExportData } from "./json";
import { exportToPdf } from "../scene/pdf";

let isWorkerConfigured = false;

const getBuildVersion = () => {
  const metaVersion = document
    .querySelector('meta[name="version"]')
    ?.getAttribute("content");

  if (window.__EXCALIDRAW_SHA__ && window.__EXCALIDRAW_SHA__ !== "{version}") {
    return window.__EXCALIDRAW_SHA__;
  }

  if (metaVersion && metaVersion !== "{version}") {
    return metaVersion;
  }

  return null;
};

const configurePdfWorker = () => {
  if (!isWorkerConfigured) {
    const workerUrl = new URL(pdfWorkerUrl, window.location.origin);
    const buildVersion = getBuildVersion();
    if (buildVersion) {
      workerUrl.searchParams.set("v", buildVersion);
    }
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl.toString();
    isWorkerConfigured = true;
  }
};

export const isPdfFile = (file: Blob | File) => {
  return (
    file.type === MIME_TYPES.pdf ||
    ("name" in file &&
      typeof file.name === "string" &&
      /\.pdf$/i.test(file.name))
  );
};

export const pdfToImageFiles = async (file: Blob | File): Promise<File[]> => {
  configurePdfWorker();

  const loadingTask = pdfjsLib.getDocument({
    data: await blobToArrayBuffer(file),
  });

  try {
    const pdf = await loadingTask.promise;
    const baseName =
      "name" in file && file.name
        ? file.name.replace(/\.pdf$/i, "") || file.name
        : "pdf";

    const imageFiles: File[] = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement("canvas");

      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));

      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Couldn't create canvas context for PDF page.");
      }

      await page.render({
        canvas,
        canvasContext: context as CanvasRenderingContext2D,
        viewport,
      }).promise;

      const blob = await canvasToBlob(canvas);
      imageFiles.push(
        new File([blob], `${baseName}-page-${pageNumber}.png`, {
          type: MIME_TYPES.png,
        }),
      );

      page.cleanup?.();
    }

    return imageFiles;
  } finally {
    loadingTask.destroy?.();
  }
};

export const saveAsPdf = async ({
  data,
  filename,
  fileHandle,
  embedSceneJSON,
}: {
  data: MaybePromise<JSONExportData>;
  filename: string;
  fileHandle: FileSystemFileHandle | null;
  embedSceneJSON?: string | null;
}) => {
  const saveFileHandle =
    fileHandle && getFileHandleType(fileHandle) === "pdf" ? fileHandle : null;

  const blob = Promise.resolve(data).then(async ({ elements, appState, files }) => {
    const sceneJSON =
      embedSceneJSON ?? serializeAsJSON(elements, appState, files, "local");
    const selectedElementIds = Object.fromEntries(
      elements
        .filter((element) => !element.isDeleted && element.customData?.isPapel)
        .map((element) => [element.id, true]),
    );

    const pdfBlob = await exportToPdf(
      elements,
      { ...appState, selectedElementIds } as any,
      files,
      sceneJSON,
    );

    if (!pdfBlob) {
      throw new Error("No paper elements found for PDF export.");
    }

    return pdfBlob;
  });

  const savedFileHandle = await fileSave(blob, {
    name: filename,
    extension: "pdf",
    description: "OnePaper PDF file",
    fileHandle: saveFileHandle,
  });

  return { fileHandle: savedFileHandle };
};
